export interface Tool {
  id: string;
  name: string;
  description: string;
  category: string;
  route: string;
  icon: string;
  keywords: string[];
  useCases: string[];
}

export interface ToolRecommendation {
  tool: Tool;
  relevanceScore: number;
  reasoning: string;
  suggestedFor: 'product' | 'project' | 'both';
}

export interface ProductTool {
  id: string;
  product_id: string;
  tool_id: string;
  project_id?: string;
  created_at: string;
  notes?: string;
}

export const AVAILABLE_TOOLS: Tool[] = [
  {
    id: 'business-model-canvas',
    name: 'Business Model Canvas',
    description:
      'Quickly visualize how your business creates, delivers, and captures value by mapping nine key building blocks on a single page.',
    category: 'Strategy',
    route: '/tools/business-model-canvas',
    icon: 'Grid3X3',
    keywords: [
      'business model',
      'value proposition',
      'revenue',
      'customer segments',
      'channels',
    ],
    useCases: [
      'Define value proposition',
      'Map revenue streams',
      'Identify key partnerships',
      'Understand cost structure',
    ],
  },
  {
    id: 'lean-canvas',
    name: 'Lean Canvas',
    description:
      'Explore your product idea by capturing problems, solutions, and key assumptions so you can validate ideas through rapid experimentation.',
    category: 'Strategy',
    route: '/tools/lean-canvas',
    icon: 'Workflow',
    keywords: [
      'lean startup',
      'problem solution fit',
      'mvp',
      'customer validation',
      'business model',
    ],
    useCases: [
      'Validate problem-solution fit',
      'Plan MVP features',
      'Identify key assumptions',
      'Track early metrics',
    ],
  },
  {
    id: 'customer-profile',
    name: 'Customer Profile',
    description:
      'Understand your customers by mapping their jobs, pains, and gains to uncover what truly drives their decisions.',
    category: 'Research',
    route: '/tools/customer-profile',
    icon: 'UserCircle',
    keywords: ['customer', 'persona', 'audience', 'demographics', 'behavior'],
    useCases: [
      'Define target audience',
      'Create user personas',
      'Understand customer needs',
      'Map customer journey',
    ],
  },
  {
    id: 'customer-persona',
    name: 'Customer Persona',
    description:
      'Expand your customer profiles by adding detailed demographics, interests, and estimated audience size.',
    category: 'Research',
    route: '/tools/customer-persona',
    icon: 'Users',
    keywords: [
      'persona',
      'customer',
      'demographics',
      'audience',
      'user research',
    ],
    useCases: [
      'Create user personas',
      'Define target audience',
      'Map customer characteristics',
      'Understand customer priorities',
    ],
  },
  {
    id: 'customer-journey-map',
    name: 'Customer Journey Map',
    description:
      'Visualize the complete customer journey through stages, steps, emotional experiences, and insights to identify opportunities for improvement.',
    category: 'Design',
    route: '/tools/customer-journey-map',
    icon: 'Map',
    keywords: [
      'customer journey',
      'user journey',
      'experience mapping',
      'touchpoints',
      'customer experience',
      'journey stages',
      'pain points',
      'gains',
    ],
    useCases: [
      'Map customer journey stages',
      'Identify pain points and gains',
      'Visualize customer touchpoints',
      'Improve customer experience',
    ],
  },
  {
    id: 'product-strategy-canvas',
    name: 'Product Strategy Canvas',
    description:
      'Map your product vision, goals, success measures, and ideas to show how your strategy supports the company mission and adapts as you learn.',
    category: 'Strategy',
    route: '/tools/product-strategy-canvas',
    icon: 'Compass',
    keywords: [
      'product strategy',
      'vision',
      'goals',
      'measures',
      'ideas',
      'mission alignment',
    ],
    useCases: [
      'Define product vision',
      'Set strategic goals',
      'Align with company mission',
      'Plan product roadmap',
    ],
  },
  {
    id: 'knowns-unknowns',
    name: 'Knowns & Unknowns',
    description:
      "Document what you know and don't know about your product idea to identify the riskiest assumptions to validate.",
    category: 'Research',
    route: '/tools/knowns-unknowns',
    icon: 'CircleHelp',
    keywords: [
      'assumptions',
      'risks',
      'uncertainty',
      'validation',
      'knowns',
      'unknowns',
      'desirability',
      'viability',
      'feasibility',
    ],
    useCases: [
      'Identify key assumptions',
      'Assess product risks',
      'Prioritize validation efforts',
      'Track uncertainty reduction',
    ],
  },
  {
    id: 'value-proposition-canvas',
    name: 'Value Proposition Canvas',
    description:
      'Articulate how your products and services create value for customers and differentiate from competitors using a simple ad-lib structure.',
    category: 'Strategy',
    route: '/tools/value-proposition-canvas',
    icon: 'Gem',
    keywords: [
      'value proposition',
      'customer segment',
      'jobs to be done',
      'customer pains',
      'customer gains',
      'competing value proposition',
    ],
    useCases: [
      'Define value proposition',
      'Map customer segments',
      'Identify customer pains and gains',
      'Differentiate from competitors',
    ],
  },
  {
    id: 'five-whys',
    name: '5 Whys',
    description:
      'Identify root causes by asking "why" repeatedly to drill down from symptoms to underlying problems.',
    category: 'Research',
    route: '/tools/five-whys',
    icon: 'CircleHelp',
    keywords: [
      'root cause',
      'problem solving',
      'analysis',
      'why',
      'investigation',
      'troubleshooting',
      'diagnosis',
    ],
    useCases: [
      'Identify root causes of problems',
      'Dig deeper into symptoms',
      'Analyze problem chains',
      'Find underlying issues',
    ],
  },
  {
    id: 'opportunity-solution-tree',
    name: 'Opportunity Solution Tree',
    description:
      'Break down business goals into opportunities, solutions, and experiments to systematically explore and validate approaches.',
    category: 'Strategy',
    route: '/tools/opportunity-solution-tree',
    icon: 'TreePine',
    keywords: [
      'opportunity',
      'solution',
      'experiment',
      'business goal',
      'validation',
      'hypothesis',
      'testing',
      'strategy',
    ],
    useCases: [
      'Break down business goals into actionable opportunities',
      'Explore multiple solutions for each opportunity',
      'Design experiments to validate solutions',
      'Structure product discovery and validation',
    ],
  },
  {
    id: 'user-story-map',
    name: 'User Story Map',
    description:
      'Organize user activities, backbone tasks, and user tasks in a hierarchical structure to visualize the user journey and plan product releases.',
    category: 'Design',
    route: '/tools/user-story-map',
    icon: 'Book',
    keywords: [
      'user story',
      'user activities',
      'backbone tasks',
      'user tasks',
      'story mapping',
      'release planning',
      'user journey',
    ],
    useCases: [
      'Organize user activities',
      'Map backbone tasks',
      'Plan user tasks',
      'Structure product releases',
    ],
  },
];

export type ToolId = (typeof AVAILABLE_TOOLS)[number]['id'];
